const REASONS = [
  {
    icon: "ri-money-dollar-circle-line",
    title: "Revenue-Focused Design",
    desc: "We don't just build pretty websites. Every design decision is made to convert visitors into paying customers.",
  },
  {
    icon: "ri-smartphone-line",
    title: "Mobile-First Always",
    desc: "Over 90% of your customers are on mobile. We design mobile-first so you never miss a potential client.",
  },
  {
    icon: "ri-speed-line",
    title: "Modern & Ultra-Fast",
    desc: "Slow websites kill conversions. We build blazing-fast sites that keep visitors engaged and search engines happy.",
  },
  {
    icon: "ri-translate-2",
    title: "Se Habla Español",
    desc: "Atendemos a toda la comunidad hispana. Hablamos tu idioma para que cada detalle de tu negocio quede exactamente como lo imaginas.",
  },
];

const CLIENTS = [
  { icon: "ri-store-2-line", type: "Small Businesses", location: "Miami & Online", desc: "Brick-and-mortar businesses ready to grow their digital presence and attract more foot traffic." },
  { icon: "ri-user-star-line", type: "Entrepreneurs", location: "Worldwide", desc: "Visionary entrepreneurs who need a powerful online brand to match their ambitions and scale fast." },
  { icon: "ri-map-pin-2-line", type: "Local Service Providers", location: "Miami, FL", desc: "Local services — from barbers to contractors — who want to dominate their market online." },
];

export default function WhyChooseUs() {
  return (
    <section id="why-us" className="bg-[#0A1628] py-24 md:py-32 relative overflow-hidden">
      {/* Grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      <div className="absolute bottom-0 right-0 w-96 h-96 opacity-10 blur-3xl bg-[#D4AF37] rounded-full" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        {/* Section Label */}
        <div className="flex items-center gap-2 mb-5">
          <div className="h-px w-10 bg-[#D4AF37]" />
          <span className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Why Web2Revenue
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-24 items-center mb-24">
          {/* Left */}
          <div>
            <h2
              className="text-white font-black text-4xl md:text-5xl leading-tight mb-6"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              We Don&apos;t Just Build
              <br />
              Websites — We Build
              <br />
              <span
                style={{
                  background: "linear-gradient(135deg, #D4AF37 0%, #f5d980 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Revenue Systems
              </span>
            </h2>
            <p className="text-white/50 text-lg leading-relaxed max-w-md" style={{ fontFamily: "'Inter', sans-serif" }}>
              Designed for real business growth, not just pretty pixels. We understand that your website&apos;s job is to make you money.
            </p>
          </div>

          {/* Right — Feature Cards */}
          <div className="flex flex-col gap-5">
            {REASONS.map((reason, i) => (
              <div
                key={i}
                className="flex items-start gap-5 bg-white/5 border border-[#D4AF37]/15 rounded-2xl p-6 hover:border-[#D4AF37]/40 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#D4AF37]/10 group-hover:bg-[#D4AF37]/20 transition-colors duration-300 shrink-0">
                  <i className={`${reason.icon} text-[#D4AF37] text-2xl`} />
                </div>
                <div>
                  <h3 className="text-white font-bold text-base mb-1.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {reason.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {reason.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Bar */}
        <div className="border border-[#D4AF37]/20 rounded-2xl p-8 grid grid-cols-1 sm:grid-cols-3 gap-8 bg-white/[0.03]">
          {[
            { value: "50+", label: "Websites Delivered" },
            { value: "100%", label: "Mobile Optimized" },
            { value: "24/7", label: "Support Available" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className="text-5xl font-black text-[#D4AF37] mb-2"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {stat.value}
              </div>
              <div className="text-white/40 text-sm tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Target Clients */}
        <div className="mt-24">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Who We Serve
            </span>
            <h3 className="text-white font-black text-3xl md:text-4xl mt-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Built for Ambitious Businesses
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CLIENTS.map((client, i) => (
              <div
                key={i}
                className="bg-white/5 border-l-4 border-l-[#D4AF37] border border-[#D4AF37]/10 rounded-2xl p-7 hover:bg-white/10 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#D4AF37]/10 mb-5">
                  <i className={`${client.icon} text-[#D4AF37] text-2xl`} />
                </div>
                <h4 className="text-white font-bold text-xl mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {client.type}
                </h4>
                <div className="inline-flex items-center gap-1.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-full px-3 py-1 mb-4">
                  <i className="ri-map-pin-line text-[#D4AF37] text-xs" />
                  <span className="text-[#D4AF37] text-xs font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {client.location}
                  </span>
                </div>
                <p className="text-white/50 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {client.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
